import { useState, useEffect, useCallback } from "react";
import { apiV1 } from "api-v2";

interface PreflightResponse {
  masked_email: string;
  invitee_name?: string | null;
  company_name?: string | null;
  invited_by_name?: string | null;
  invited_by_email?: string | null;
  invite_expires_at?: string | null;
}

export type PreflightErrorCode =
  | "INVITE_TOKEN_INVALID"
  | "INVITE_TOKEN_EXPIRED"
  | "VERIFICATION_LOCKED"
  | "RATE_LIMIT_EXCEEDED"
  | "PREFLIGHT_FAILED";

// Server-issued business codes the API returns in `response.data.code`.
// Anything else (5xx, network failure, missing body, unknown code) is a
// transient/unexpected failure — surface as PREFLIGHT_FAILED rather than
// conflating with INVITE_TOKEN_INVALID. See PRO-509: a missing rate-limiter
// on the backend produced a 500 with no `code` body, which previously fell
// through to INVITE_TOKEN_INVALID and showed users a misleading
// "invite expired" page.
const KNOWN_BUSINESS_CODES: ReadonlyArray<PreflightErrorCode> = [
  "INVITE_TOKEN_INVALID",
  "INVITE_TOKEN_EXPIRED",
  "VERIFICATION_LOCKED",
  "RATE_LIMIT_EXCEEDED",
];

interface UsePreflightInviteTokenResult {
  maskedEmail: string | null;
  inviteeName: string | null;
  companyName: string | null;
  invitedByName: string | null;
  invitedByEmail: string | null;
  inviteExpiresAt: string | null;
  isLoading: boolean;
  isExpired: boolean;
  errorCode: PreflightErrorCode | null;
  retry: () => void;
}

/**
 * Cheap, idempotent token check on mount. Calls the preflight endpoint —
 * which validates the token and returns the masked email WITHOUT issuing a
 * verification code or sending an email. Safe under React StrictMode
 * double-mount, refresh, or browser retry.
 *
 * The actual code dispatch is handled by useIssueVerificationCodeMutation,
 * which the user triggers from the email-confirmation step.
 */
export const usePreflightInviteToken = (token: string | null): UsePreflightInviteTokenResult => {
  const [maskedEmail, setMaskedEmail] = useState<string | null>(null);
  const [inviteeName, setInviteeName] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [invitedByName, setInvitedByName] = useState<string | null>(null);
  const [invitedByEmail, setInvitedByEmail] = useState<string | null>(null);
  const [inviteExpiresAt, setInviteExpiresAt] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [errorCode, setErrorCode] = useState<PreflightErrorCode | null>(null);
  // Bumping this triggers a re-fetch via the effect dep array — gives us a
  // clean "retry" handle without duplicating the request logic.
  const [retryNonce, setRetryNonce] = useState(0);

  const retry = useCallback(() => {
    setErrorCode(null);
    setIsExpired(false);
    setRetryNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!token) {
      setIsExpired(true);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    apiV1
      .post<PreflightResponse>("auth/invite/preflight-link", { token })
      .then((res) => {
        if (!cancelled) {
          setMaskedEmail(res.data.masked_email);
          setInviteeName(res.data.invitee_name ?? null);
          setCompanyName(res.data.company_name ?? null);
          setInvitedByName(res.data.invited_by_name ?? null);
          setInvitedByEmail(res.data.invited_by_email ?? null);
          setInviteExpiresAt(res.data.invite_expires_at ?? null);
        }
      })
      .catch((err) => {
        if (cancelled) {
          return;
        }
        const rawCode = err?.response?.data?.code as string | undefined;
        const status = err?.response?.status as number | undefined;
        const isKnown =
          typeof rawCode === "string" &&
          (KNOWN_BUSINESS_CODES as ReadonlyArray<string>).includes(rawCode);

        // Treat 5xx, no-response (network), or unknown/missing business code
        // as transient: PREFLIGHT_FAILED. Only set isExpired for genuine
        // token-state errors so the user isn't told their invite is dead
        // when the server is the one having a bad day.
        let code: PreflightErrorCode;
        if (isKnown) {
          code = rawCode as PreflightErrorCode;
        } else if (status && status >= 400 && status < 500) {
          code = "INVITE_TOKEN_INVALID";
        } else {
          code = "PREFLIGHT_FAILED";
        }

        if (code === "INVITE_TOKEN_EXPIRED" || code === "INVITE_TOKEN_INVALID") {
          setIsExpired(true);
        }
        setErrorCode(code);
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [token, retryNonce]);

  return {
    maskedEmail,
    inviteeName,
    companyName,
    invitedByName,
    invitedByEmail,
    inviteExpiresAt,
    isLoading,
    isExpired,
    errorCode,
    retry,
  };
};
